import React, { useState, useContext } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  TextField,
  Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import {
  GroundTruthMultiChoice,
  QuestionMultiChoice,
  QuestionTypeV2,
  PartialBy,
  GTMultiChoicePayloadRelax,
  GTMultiChoicePayloadStrict,
} from '../../redux/types';
import ColorContext from '../../context/ColorContext';
import GTResetButton from './GTResetButton';

type Props = {
  groundTruthId?: number;
  surveyId: number;
  sectionId: number;
  questionId: number;
  caseId: number;
  question: QuestionMultiChoice;
  payload?: GroundTruthMultiChoice['payload'];
  gtGroupId: number|null;
  onSave: (gtData: PartialBy<GroundTruthMultiChoice, 'id'>) => void;
  onCancel: () => void;
  onDelete: (dgtId: number) => void;
};

const getInitalPayloadRelax = (
  question: QuestionMultiChoice,
): GTMultiChoicePayloadRelax | undefined => {
  const initPayload = question.payload?.reduce((acc, cur) => {
    acc.truths = {
      ...acc.truths,
      [cur.id]: 0,
    };
    acc.strict = false;
    return acc;
  }, {} as GTMultiChoicePayloadRelax);
  return initPayload;
};

const getInitalPayloadStrict = (
  question: QuestionMultiChoice,
): GTMultiChoicePayloadStrict | undefined => {
  const initPayload = question.payload?.reduce((acc, cur) => {
    acc.truths = {
      ...acc.truths,
      [cur.id]: false,
    };
    acc.strict = true;
    acc.score = 0;
    return acc;
  }, {} as GTMultiChoicePayloadStrict);
  return initPayload;
};

const GTMultiChoiceEditor: React.FC<Props> = ({
  groundTruthId,
  surveyId,
  sectionId,
  questionId,
  caseId,
  question,
  payload,
  gtGroupId,
  onSave,
  onCancel,
  onDelete,
}: Props) => {
  const color = useContext(ColorContext);
  const [payloadBuffer, setpayloadBuffer] = useState<GroundTruthMultiChoice['payload'] | undefined>(payload || getInitalPayloadRelax(question));
  const [strict, setStrict] = useState<boolean>(payload?.strict || false);

  const saveHandler = () => {
    if (!payloadBuffer) {
      return;
    }
    const newGTRecord: PartialBy<GroundTruthMultiChoice, 'id'> = {
      id: groundTruthId,
      digitalSurveyId: surveyId,
      digitalSectionId: sectionId,
      digitalQuestionId: questionId,
      digitalCaseId: caseId,
      type: QuestionTypeV2.MultiChoice,
      payload: payloadBuffer,
      digitalGroundTruthGroupId: gtGroupId,
    };
    onSave(newGTRecord);
  };

  const cancelHandler = () => {
    onCancel();
  };

  const onOptionScoreChangeRelax = (
    event: React.ChangeEvent<HTMLInputElement>,
    optionId: number,
  ) => {
    if (payloadBuffer?.strict === true) {
      return;
    }
    const score = parseInt(event.target.value, 10);
    setpayloadBuffer({
      ...payloadBuffer,
      truths: {
        ...payloadBuffer?.truths,
        [optionId]: score,
      },
      strict: false,
    } as GTMultiChoicePayloadRelax);
  };

  const handleStrictChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const strictMode = event.target.checked;
    setStrict(strictMode);
    if (strictMode) {
      setpayloadBuffer(getInitalPayloadStrict(question));
    } else {
      setpayloadBuffer(getInitalPayloadRelax(question));
    }
  };

  const handleTruthChange = (event: React.ChangeEvent<HTMLInputElement>, optionId: number) => {
    const check = event.target.checked;
    if (payloadBuffer?.strict) {
      setpayloadBuffer({
        ...payloadBuffer,
        truths: {
          ...payloadBuffer.truths,
          [optionId]: check,
        },
      });
    }
  };

  const handleScoreChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const score = Number(e.target.value);
    if (payloadBuffer?.strict) {
      setpayloadBuffer({
        ...payloadBuffer,
        score,
      });
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end">
        <FormControlLabel
          control={
            <Checkbox onChange={handleStrictChange} checked={strict} />
          }
          label="Strict Mode"
        />
      </Box>
      <Box sx={{ mx: 2, mb: 3 }}>
        {
          payloadBuffer && !payloadBuffer.strict && (
            <Box>
              <Typography color="textSecondary">{question.text}</Typography>
              {
                question.payload?.map((op) => (
                  <Box display="flex" alignItems="flex-end" gap={3} key={op.id} sx={{ my: 1 }}>
                    <Typography gutterBottom component="p" sx={{ flexBasis: 500 }}>{op.text}</Typography>
                    <TextField
                      type="number"
                      size="small"
                      value={payloadBuffer.truths?.[op.id]}
                      onChange={(
                        e: React.ChangeEvent<HTMLInputElement>,
                      ) => onOptionScoreChangeRelax(e, op.id)}
                    />
                  </Box>
                ))
              }
            </Box>
          )
        }
        {
          payloadBuffer && payloadBuffer.strict && (
            <Box>
              <FormControl component="fieldset" variant="standard">
                <FormLabel component="legend">{question.text}</FormLabel>
                <FormGroup>
                  {
                    question.payload?.map((op) => (
                      <FormControlLabel
                        key={op.id}
                        control={(
                          <Checkbox
                            checked={payloadBuffer.truths[op.id]}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => { handleTruthChange(e, op.id); }}
                          />
                        )}
                        label={op.text}
                      />
                    ))
                  }
                </FormGroup>
              </FormControl>
              <Box sx={{ my: 2 }}>
                <TextField
                  type="number"
                  size="small"
                  label="Score"
                  value={payloadBuffer.score}
                  onChange={handleScoreChange}
                />
              </Box>
            </Box>
          )
        }
      </Box>
      <Divider />
      <Box display="flex" sx={{ mt: 1 }} justifyContent="flex-end" gap={1}>
        <Box flexGrow={1}>
          <GTResetButton groundTruthId={groundTruthId} onReset={onDelete} />
        </Box>
        <Button variant="contained" color={color} onClick={saveHandler}><FormattedMessage id="save" /></Button>
        <Button variant="contained" color="secondary" onClick={cancelHandler}><FormattedMessage id="genericCancel" /></Button>
      </Box>
    </Box>
  );
};

export default GTMultiChoiceEditor;
